import { Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as HorizontalIcon } from 'assets/icons/distribute_horizontal.svg';
import { ReactComponent as VerticalIcon } from 'assets/icons/distribute_vertical.svg';
import { useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DistributeOption } from './AlignmentMenu.types';
import { checkIsDistributeOptionDisabled } from './Distribute.helpers';

interface DistributeOptionsProps {
  onDistribute: (distributeOption: DistributeOption) => void;
}

const DISTRIBUTE_OPTIONS = [
  {
    label: 'vertically',
    icon: <VerticalIcon />,
    value: DistributeOption.Vertical,
  },
  {
    label: 'horizontally',
    icon: <HorizontalIcon />,
    value: DistributeOption.Horizontal,
  },
];

export const DistributeOptions = ({ onDistribute }: DistributeOptionsProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'distribute' });
  const header = t('label');
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const isDisabled = activeWidgets.length < 3;

  return (
    <Box mt={2}>
      <Text fontSize='sm' fontWeight='semibold'>
        {header}
      </Text>
      <Tooltip
        fontSize='xs'
        label='Select at least 3 elements to distribute'
        isDisabled={!isDisabled}
        hasArrow
        placement='right'
      >
        <Flex justifyContent='space-between' mt={2}>
          {DISTRIBUTE_OPTIONS.map(({ label, icon, value }) => {
            return (
              <DistributeOptionButton
                label={t(`options.${label}` as keyof typeof t)}
                icon={icon}
                value={value}
                isDisabled={isDisabled}
                onClick={onDistribute}
                key={value}
              />
            );
          })}
        </Flex>
      </Tooltip>
    </Box>
  );
};

interface DistributeOptionButtonProps {
  label: string;
  icon: ReactElement;
  value: DistributeOption;
  onClick: (direction: DistributeOption) => void;
  isDisabled: boolean;
}

const DistributeOptionButton = ({ label, icon, value, onClick, isDisabled }: DistributeOptionButtonProps) => {
  const isOptionDisabled = isDisabled || checkIsDistributeOptionDisabled(value);

  const handleClick = (e: MouseEvent) => {
    if (isOptionDisabled) {
      e.preventDefault();
      return;
    }
    onClick(value);
  };

  return (
    <Button
      variant={'ghost'}
      isDisabled={isOptionDisabled}
      p={1}
      justifyContent='flex-start'
      size='sm'
      fontWeight='normal'
      leftIcon={icon}
      onClick={handleClick}
      key={value}
      w='50%'
    >
      {label}
    </Button>
  );
};
